/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useRef} from 'react';
import Chip from '@material-ui/core/Chip';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {BotIcon} from '../../icons/BotIcon';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {useSpaciousTooltipStyles} from '../tooltip/InfoTooltip';
import Tooltip from '../tooltip/Tooltip';

const EYE_MAX_OFFSET    = 2;
const BLINK_INTERVAL_MS = 3000;
const BLINK_CLOSE_MS    = 120;

const clampOffset = (value: number, max: number): number => (
  Math.max(-max, Math.min(max, value))
);

const useStyles = makeStyles(() => ({
  badge : {
    borderRadius    : '4px',
    padding         : '2px 8px',
    height          : '32px',
    verticalAlign   : 'middle',
    backgroundColor : dlsColor('darkTeal'),
    border          : `1.5px solid ${dlsColor('aaBlue')}`
  },
  badgeLabel : {
    ...dlsAlt('typography', 'cta2'),
    color    : dlsColor('white'),
    padding  : '0px 0px 0px 8px',
    fontSize : '14px'
  }
}));

interface M2MBadgeProps {
  className?: string;
}

/**
 * M2MBadge renders a read-only chip indicating that a user is a
 * Machine-to-Machine (M2M) service account, with a tooltip explaining
 * the designation.
 *
 * ```typescript
 * import {M2MBadge} from '@ion/dls/src/components/badges/M2MBadge';
 * ...
 * <M2MBadge />
 * ```
 */
export const M2MBadge = ({className}: M2MBadgeProps) => {
  const classes                = useStyles();
  const spaciousTooltipClasses = useSpaciousTooltipStyles();
  const eyesRef                = useRef<SVGGElement>(null);
  const iconRef                = useRef<SVGSVGElement>(null);
  const blinkRef               = useRef<SVGGElement>(null);
  const blinkIntervalRef       = useRef<number | null>(null);
  const blinkTimeoutRef        = useRef<number | null>(null);

  useEffect(function() {
    return function() {
      if (blinkIntervalRef.current) {
        window.clearInterval(blinkIntervalRef.current);
      }
      if (blinkTimeoutRef.current) {
        window.clearTimeout(blinkTimeoutRef.current);
      }
    };
  }, []);

  function triggerBlink() {
    if (blinkRef.current) {
      blinkRef.current.style.transform = 'scaleY(0.1)';
      blinkTimeoutRef.current = window.setTimeout(function() {
        if (blinkRef.current) {
          blinkRef.current.style.transform = 'scaleY(1)';
        }
      }, BLINK_CLOSE_MS);
    }
  }

  function handleHoverStart() {
    if (!blinkIntervalRef.current) {
      blinkIntervalRef.current = window.setInterval(triggerBlink, BLINK_INTERVAL_MS);
    }
  }

  function handleEyeMove(event: React.MouseEvent<HTMLSpanElement>) {
    if (eyesRef.current && iconRef.current) {
      const bounds  = iconRef.current.getBoundingClientRect();
      const offsetX = clampOffset(
        ((event.clientX - (bounds.left + bounds.width / 2)) / (bounds.width / 2)) * EYE_MAX_OFFSET,
        EYE_MAX_OFFSET
      );
      const offsetY = clampOffset(
        ((event.clientY - (bounds.top + bounds.height / 2)) / (bounds.height / 2)) * EYE_MAX_OFFSET,
        EYE_MAX_OFFSET
      );
      eyesRef.current.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    }
  }

  function handleEyeReset() {
    if (eyesRef.current) {
      eyesRef.current.style.transform = 'translate(0px, 0px)';
    }
    if (blinkIntervalRef.current) {
      window.clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
    }
    if (blinkTimeoutRef.current) {
      window.clearTimeout(blinkTimeoutRef.current);
      blinkTimeoutRef.current = null;
    }
    if (blinkRef.current) {
      blinkRef.current.style.transform = 'scaleY(1)';
    }
  }

  return (
    <span onMouseEnter={handleHoverStart}
      onMouseMove={handleEyeMove}
      onMouseLeave={handleEyeReset}>
      <Tooltip title={translate('common:editors.userType.m2mBadgeTooltip')}
        externalClasses={spaciousTooltipClasses}
        arrow>
        <span>
          <Chip component='span'
            className={className ? classnames(classes.badge, className) : classes.badge}
            classes={{label: classes.badgeLabel}}
            icon={
              <BotIcon color={dlsColor('white')}
                size={24}
                eyesRef={eyesRef}
                blinkRef={blinkRef}
                svgRef={iconRef} />
            }
            label={translate('common:editors.userType.m2mLabel')} />
        </span>
      </Tooltip>
    </span>
  );
};

export default memo(M2MBadge);
